/**
 * audioPeaks — decode an audio file in the browser and downsample it to a small
 * fixed-length amplitude envelope (the "peaks") for the waveform player. Browser-
 * only (needs Web Audio's `decodeAudioData`); the editor calls it once at upload
 * and stores the result in the item's contentProps, so the published site draws
 * the same waveform with no client decode.
 *
 * The peaks are N ints 0..100 (~1.3 KB JSON) — small enough to inline into the
 * serialized page. Failure (unsupported codec, corrupt bytes) never throws to the
 * caller: it returns a flat placeholder so upload still succeeds and the widget
 * falls back to a native `<audio>` for playback.
 */
import { FLAT_PLACEHOLDER } from "../waveformSvg.js";

/** Bucket count — matches the waveform's bar count. */
export const PEAK_BUCKETS = 400;

export interface AudioPeaks {
  /** Normalized amplitude per bucket, ints 0..100. */
  readonly peaks: number[];
  /** Clip length in seconds (0 if undecodable). */
  readonly duration: number;
}

// Below this normalized max the clip is effectively silent — don't amplify the
// noise floor to full-height bars; show a flat low envelope instead.
const SILENCE_EPS = 1e-4;

function decodeCtor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  );
}

/**
 * Decode `file` and return its {peaks, duration}. Resolves to a flat placeholder
 * (never rejects) when decoding isn't possible, so callers can always proceed.
 */
export async function extractPeaks(file: Blob): Promise<AudioPeaks> {
  const Ctor = decodeCtor();
  if (!Ctor) return { peaks: [...FLAT_PLACEHOLDER], duration: 0 };
  let ctx: AudioContext | null = null;
  try {
    const bytes = await file.arrayBuffer();
    ctx = new Ctor();
    const buf = await ctx.decodeAudioData(bytes);
    return { peaks: downsample(buf), duration: buf.duration };
  } catch {
    return { peaks: [...FLAT_PLACEHOLDER], duration: 0 };
  } finally {
    // Free the hardware context; ignore if already closed.
    void ctx?.close().catch(() => {});
  }
}

/** Max-|amplitude| per bucket across all channels, normalized to 0..100. */
function downsample(buf: AudioBuffer): number[] {
  const total = buf.length;
  const channels: Float32Array[] = [];
  for (let c = 0; c < buf.numberOfChannels; c++)
    channels.push(buf.getChannelData(c));
  const block = Math.max(1, Math.floor(total / PEAK_BUCKETS));
  const raw = new Array<number>(PEAK_BUCKETS).fill(0);
  let max = 0;
  for (let i = 0; i < PEAK_BUCKETS; i++) {
    const start = i * block;
    let peak = 0;
    for (let j = 0; j < block; j++) {
      const idx = start + j;
      if (idx >= total) break;
      for (const ch of channels) {
        const v = Math.abs(ch[idx]);
        if (v > peak) peak = v;
      }
    }
    raw[i] = peak;
    if (peak > max) max = peak;
  }
  if (max < SILENCE_EPS) return raw.map(() => 4); // near-silent → flat low bars
  return raw.map((v) => Math.round((v / max) * 100));
}
